import { useAuthStore, useRadioSettingsStore } from '@/stores'
import { watch } from 'vue'
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'

const routes: RouteRecordRaw[] = [
  { path: '/', redirect: '/app/shows' },
  { path: '/login', name: 'login', component: () => import('@/pages/LoginPage.vue') },
  {
    path: '/app',
    component: () => import('./components/AApplicationFrame.vue'),
    beforeEnter: async (to, from, next) => {
      const radioSettingsStore = useRadioSettingsStore()
      await radioSettingsStore.isSynced
      next()
    },
    children: [
      // Entrypoint for steering’s logout redirect route. Not used internally.
      { path: 'logout', name: 'logout', component: () => import('@/pages/LogoutPage.vue') },
      { path: 'credits', name: 'credits', component: () => import('@/pages/CreditsPage.vue') },
      {
        path: 'shows',
        children: [
          {
            path: '',
            name: 'show-list',
            component: () => import('@/pages/ShowListPage.vue'),
          },
          {
            path: ':showId',
            name: 'show',
            component: () => import('@/pages/_ShowView.vue'),
            redirect: (to) => ({
              name: 'show-timeslot-list',
              params: { showId: to.params.showId },
            }),
            children: [
              {
                path: 'settings',
                name: 'show-settings',
                component: () => import('@/pages/ShowSettingsPage.vue'),
              },
              {
                path: 'episodes',
                children: [
                  {
                    path: '',
                    name: 'show-episode-list',
                    component: () => import('@/pages/ShowEpisodeListPage.vue'),
                  },
                  {
                    path: ':episodeId',
                    name: 'episode',
                    component: () => import('@/pages/_EpisodeView.vue'),
                    redirect: (to) => ({
                      name: 'episode-details',
                      params: to.params,
                    }),
                    children: [
                      {
                        path: '',
                        name: 'episode-details',
                        component: () => import('@/pages/EpisodeDetailsPage.vue'),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'schedules',
                children: [
                  {
                    path: '',
                    name: 'show-schedule-list',
                    component: () => import('@/pages/ShowScheduleListPage.vue'),
                  },
                  {
                    path: ':scheduleId',
                    name: 'schedule',
                    component: () => import('@/pages/_ScheduleView.vue'),
                    redirect: (to) => ({
                      name: 'schedule-details',
                      params: to.params,
                    }),
                    children: [
                      {
                        path: '',
                        name: 'schedule-details',
                        component: () => import('@/pages/ScheduleDetailsPage.vue'),
                      },
                    ],
                  },
                ],
              },
              {
                path: 'timeslots',
                children: [
                  {
                    path: '',
                    name: 'show-timeslot-list',
                    component: () => import('@/pages/ShowTimeslotListPage.vue'),
                  },
                  {
                    path: ':timeslotId',
                    name: 'timeslot',
                    component: () => import('@/pages/_TimeslotView.vue'),
                    redirect: (to) => ({
                      name: 'timeslot-details',
                      params: to.params,
                    }),
                    children: [
                      {
                        path: '',
                        name: 'timeslot-details',
                        component: () => import('@/pages/EpisodeDetailsPage.vue'),
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'my',
        component: () => import('@/pages/_AccountView.vue'),
        children: [
          {
            path: 'account/settings',
            name: 'account-settings',
            component: () => import('@/pages/AccountSettingsPage.vue'),
          },
        ],
      },
      {
        path: 'profiles',
        children: [
          {
            path: '',
            name: 'profile-list',
            component: () => import('@/pages/ProfileListPage.vue'),
          },
          {
            path: ':profileId',
            name: 'profile',
            component: () => import('@/pages/_ProfileView.vue'),
            redirect: (to) => ({ name: 'profile-details', params: to.params }),
            children: [
              {
                path: 'details',
                name: 'profile-details',
                component: () => import('@/pages/ProfileDetailsPage.vue'),
              },
            ],
          },
        ],
      },
      {
        name: 'program',
        path: 'program',
        redirect: { name: 'program-calendar-week' },
        children: [
          {
            path: 'prolongation',
            name: 'program-prolongation',
            component: () => import('@/pages/ProgramProlongationPage.vue'),
          },
          {
            path: 'fallback',
            name: 'program-fallback',
            component: () => import('@/pages/FallbackProgramWeekCalendarPage.vue'),
          },
          {
            path: 'program-calendar',
            redirect: { name: 'program-calendar-week' },
            children: [
              {
                path: 'week',
                name: 'program-calendar-week',
                component: () => import('@/pages/ProgramWeekCalendarPage.vue'),
              },
              {
                path: 'day',
                name: 'program-calendar-day',
                component: () => import('@/pages/ProgramDayCalendarPage.vue'),
              },
            ],
          },
        ],
      },
    ],
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

function redirectUnauthenticatedRequestsToLogin() {
  const authStore = useAuthStore()
  if (authStore.steeringUser) return
  redirectToLogin()
}

export function redirectToLogin() {
  const currentUrl = encodeURIComponent(location.pathname + location.search + location.hash)
  const redirectRoute = { name: 'login', query: { next: currentUrl } }
  void router.push(redirectRoute)
}

export function registerLoginChecks() {
  router.beforeEach((to) => {
    if (to.fullPath.startsWith('/app/')) {
      redirectUnauthenticatedRequestsToLogin()
    }
  })
  const authStore = useAuthStore()
  watch(() => authStore.steeringUser, redirectUnauthenticatedRequestsToLogin)
}

export default router
